import { Location } from '@angular/common';
import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, ReportService, UtilityService } from 'app/core/core.module';
import { Ach, AchAttachment, AchTransaction, Note } from 'app/core/model/entity-model';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { environment } from 'environments/environment';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { AchTransactionDialogComponent } from '../ach-transaction-dialog/ach-transaction-dialog.component';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { BulkEditAchTransactionDialogComponent } from '../bulk-edit-ach-transaction-dialog/bulk-edit-ach-transaction-dialog.component';

@Component({
    selector: 'app-ach-detail',
    templateUrl: './ach-detail.component.html',
    styleUrls: ['./ach-detail.component.scss'],
    standalone: false,
})
export class AchDetailComponent implements OnInit {
    uow = inject(GenesisUnitOfWork);
    route = inject(ActivatedRoute);
    router = inject(Router);
    auth = inject(AuthService);
    appInfo = inject(AppInformationService);
    location = inject(Location);
    utilityService = inject(UtilityService);
    reportService = inject(ReportService);
    modalService = inject(BsModalService);

    result: Subject<any> = new Subject<any>();
    achId: any;
    ach: Ach;
    achAttachments: AchAttachment[];
    achTransactions: AchTransaction[];
    initialAchInitiatedValue: Date;
    isBankInfoEncoded: boolean = true;
    bankInfo: any;
    canSeeEncodeButton: boolean;
    canSetInitiatedAndAcknowledged: boolean;
    notes: any[];

    uploadUrl = `${environment.serviceName}/file/SaveAchAttachmentToBlobStorage`;
    uploadHeader: any;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }

    async ngOnInit() {
        this.uploadHeader = { Authorization: `Bearer ${this.auth.currentUser.jwtToken}` };
        this.canSeeEncodeButton = this.auth.hasRole('Payments:AchAdmin');
        this.canSetInitiatedAndAcknowledged = this.auth.hasRole('Payments:AchAdmin');

        if (!this.achId) this.achId = this.route.snapshot.params.id;
        await this.getData();
        this.setPermissions();
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    setPermissions() {}

    async refresh() {
        this.appInfo.pleaseWaitShow();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async getData() {
        this.ach = (await this.uow.aches.byId(this.achId, 'dealer.salesRep, confirmation.order, achTransactions.invoice')) as Ach;
        this.initialAchInitiatedValue = this.ach.bankTransferInitiated;
        this.achTransactions = this.ach.achTransactions.filter((transaction) => !transaction.deleted);
        await this.getAttachments();
        await this.getBankInfo();
        this.notes = await this.uow.getNotes('Ach', this.ach.id);
    }

    async getBankInfo() {
        if (this.isBankInfoEncoded) {
            this.bankInfo = {
                accountType: this.ach.accountType,
                accountName: this.ach.accountName,
                bankName: this.ach.bankName,
                accountNumber: this.ach.accountNumber,
                routingNumber: this.ach.routingNumber,
            };
        } else {
            this.bankInfo = await this.uow.getAchBankInfo(this.ach.code);
        }
    }

    async getAttachments() {
        this.achAttachments = await this.uow.getAchAttachments(this.ach.id);
    }

    async toggleIsBankInfoEncoded() {
        this.isBankInfoEncoded = !this.isBankInfoEncoded;
        await this.getBankInfo();
    }

    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.dataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
    }

    async rowPrepare(e) {
        if (e.rowType !== 'data') return;

        const ach = e.data;
        // // console.log(ach);
        // if (ach.confirmation?.declined) {
        //     e.rowElement.className = e.rowElement.className + ' declined-to-acknowledge';
        // } else if (!ach.approved && !ach.bankTransferInitiated) {
        //     e.rowElement.className = e.rowElement.className + ' waiting-on-dealer-ach';
        // // } else if (ach.detailsNotComplete && ach.confirmation.confirmationPaymentMethodId == ConfirmationPaymentMethod.Methods.COD && !ach.bankTransferInitiated) {
        // //     e.rowElement.className = e.rowElement.className + ' waiting-on-ach-order-details';
        // } else if (
        //     !!ach.approved &&
        //     // (!ach.detailsNotComplete || ach.confirmation.confirmationPaymentMethodId != ConfirmationPaymentMethod.Methods.COD) &&
        //     !ach.bankTransferInitiated &&
        //     !ach.bankDepositAcknowledged
        // ) {
        //     e.rowElement.className = e.rowElement.className + ' ach-approved';
        if (ach && !ach.ach.approved) {
            e.rowElement.className = e.rowElement.className + ' ach-not-approved';
        } else if (ach.transferInitiated && !ach.depositAcknowledged) {
            e.rowElement.className = e.rowElement.className + ' ach-transaction-initiated';
        } else if (ach.transferInitiated && ach.depositAcknowledged) {
            e.rowElement.className = e.rowElement.className + ' ach-transaction-complete';
        }
    }

    rowDoubleClick(e) {
        this.addTransaction(e.data);
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarText',
            },
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    async openInvoice(e: any) {
        this.router.navigate([`/invoices/${e.invoiceId}`]);
    }

    onBankTransferChanged(e) {
        if (e.value) this.ach.bankTransferInitiatedBy = this.auth.currentUser.userName;
        else this.ach.bankTransferInitiatedBy = null;
    }

    onBankDepositAcknowledgedChanged(e) {
        if (e.value) this.ach.bankDepositAcknowledgedBy = this.auth.currentUser.userName;
        else this.ach.bankDepositAcknowledgedBy = null;
    }

    async save() {
        if (!this.initialAchInitiatedValue && this.ach.bankTransferInitiated) this.ach.bankTransferInitiatedBy = this.auth.currentUser.userName;
        else if (this.initialAchInitiatedValue && !this.ach.bankTransferInitiated) this.ach.bankTransferInitiatedBy = null;
        this.initialAchInitiatedValue = this.ach.bankTransferInitiated;

        this.appInfo.pleaseWaitShow();
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
    }

    cancel() {
        this.uow.rollback();
        this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }
    closeDialog() {
        this.uow.rollback();
        this.result.next({});
    }

    async setAcknowledged() {
        if (this.ach.bankDepositAcknowledged) return;

        let success = await confirm('Are you ready? A confirmation message will be emailed to the dealer.', this.appInfo.settings.company);
        if (!success) return;

        const hasEmail = await this.utilityService.checkAchPaymentConfirmationEmail(this.ach.id);
        if (!hasEmail) {
            success = await confirm('The ACH email has been cleared since the ACH was sent. Do you want to continue?', this.appInfo.settings.company);
            if (!success) return;
        }

        await this.appInfo.pleaseWaitShow();
        this.ach.bankDepositAcknowledged = new Date();
        this.ach.bankDepositAcknowledgedBy = this.auth.currentUser.userName;
        await this.save();

        if (hasEmail) {
            const response = await this.utilityService.sendAchPaymentConfirmation(this.ach.id);
            if (!response.success) await alert(response.message, 'Email not sent');
            else await alert('Email sent', this.appInfo.settings.company);
        }
        await this.appInfo.pleaseWaitHide();
    }

    async email() {
        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'ACH',
                entityId: this.ach.id,
                dealerEmail: this.ach.emailTo,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                this.appInfo.pleaseWaitShow();
                const emailSent = await this.utilityService.emailReport(result.value);
                await this.uow.commit();
                this.appInfo.pleaseWaitHide();
                await alert(emailSent ? 'Email Sent!' : 'An error occurred while sending the email.', this.appInfo.settings.company);
            }
            modalRef.hide();
        });
    }

    async print() {
        this.reportService.printAchAuthFormReport(this.ach.code);
    }

    async addTransaction(transaction?: AchTransaction) {
        const checkInitiated = transaction && !transaction.transferInitiated;

        const modalRef = this.modalService.show(AchTransactionDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                achTransaction: transaction,
                dealerId: this.ach.dealerId,
                achId: this.ach.id,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                this.appInfo.pleaseWaitShow();
                if (checkInitiated && transaction.transferInitiated) {
                    const newNote = this.uow.notes.createEntity({
                        created: new Date(),
                        createdBy: this.auth.currentUser.userName,
                        notes: `${transaction.id}: $${transaction.amount} Initiated`,
                        entityTypeName: 'Ach',
                        primaryKey: this.ach.id,
                    }) as Note;
                    await this.uow.commit([newNote]);
                }
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async bulkEditTransactions(transactions: AchTransaction[]) {
        if (!transactions || transactions.length === 0) return;
        const uninitiatedTxIds = new Set(transactions.filter((t) => !t.transferInitiated).map((t) => t.id));

        const modalRef = this.modalService.show(BulkEditAchTransactionDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                achTransactions: transactions
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();

                const newNotes: Note[] = [];
                for (const transaction of transactions) {
                    if (uninitiatedTxIds.has(transaction.id) && transaction.transferInitiated) {
                        const newNote = this.uow.notes.createEntity({
                            created: new Date(),
                            createdBy: this.auth.currentUser.userName,
                            notes: `${transaction.id}: $${transaction.amount} Initiated`,
                            entityTypeName: 'Ach',
                            primaryKey: this.ach.id,
                        }) as Note;

                        newNotes.push(newNote);
                    }
                }

                if (newNotes.length > 0) {
                    await this.uow.commit(newNotes);
                }

                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async deleteTransaction(transaction?: Ach) {
        const result = await confirm('Are you sure you want to delete the selected transaction?', this.appInfo.settings.company);
        if (!result) return;

        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;
        this.appInfo.pleaseWaitShow();
        selectedRow.deleted = new Date();
        selectedRow.deletedBy = this.auth.currentUser.userName;
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async markAsDeposited() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const selectedAmount = _.sumBy(selectedRows, (row) => row.amount);

        const result = await confirm(
            `Are you sure you want to Acknowledge Deposit of the selected transaction(s)?\r\n\r\nSelected Amount: ${selectedAmount.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            })}`,
            this.appInfo.settings.company,
        );
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        for (const row of selectedRows) {
            row.depositAcknowledged = new Date();
            row.depositAcknowledgedBy = this.auth.currentUser.userName;

            const newNote = this.uow.notes.createEntity({
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
                notes: `${row.id}: $${row.amount} Deposited`,
                entityTypeName: 'Ach',
                primaryKey: this.ach.id,
            }) as Note;
            this.notes.unshift(newNote);
        }
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    async uploaded(e) {
        if (e?.request?.responseText) {
            const fileResponses = JSON.parse(e.request.responseText) as { filename: string; name: string }[];
            for (const fileResponse of fileResponses) {
                const attachment = this.uow.achAttachments.createEntity({
                    achId: this.ach.id,
                    name: fileResponse.name,
                    filename: fileResponse.filename,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                });
                this.achAttachments.push(attachment);
                await this.uow.commit([attachment]);
            }
            this.achAttachments = _(this.achAttachments).orderBy('created', 'desc').valueOf();
        }
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        this.appInfo.pleaseWaitHide();
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    async removeAttachment(e: any, attachment: AchAttachment) {
        e.preventDefault();
        attachment.deleted = new Date();
        attachment.deletedBy = this.auth.currentUser.userName;
        this.achAttachments.splice(this.achAttachments.indexOf(attachment), 1);
        await this.uow.commit([attachment]);
    }

    openAttachment(attachment) {
        window.open(attachment.filename, '_blank');
    }

    async manuallyApprove() {
        const result = await confirm('Are you sure you want to manually approve this ACH?', this.appInfo.settings.company);
        if (!result) return;

        if (this.achAttachments.length === 0) {
            await alert('Cannot manually approve ACH without the ACH form attached.', 'Approval Error');
            return;
        }

        this.appInfo.pleaseWaitShow();
        this.ach.approved = new Date();
        this.ach.approvedBy = 'Manually Form Submitted';
        this.ach.notes += 'ACH manually approved by ' + this.auth.currentUser.userName + ' on ' + new Date().toLocaleString() + '\n';
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async addNote() {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add Note',
                mode: 'textarea',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (result.result && !!result.value) {
                const newNote = this.uow.notes.createEntity({
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    notes: result.value,
                    entityTypeName: 'Ach',
                    primaryKey: this.ach.id,
                }) as Note;
                this.uow.commit([newNote]);
                this.notes.unshift(newNote);
            }
        });
    }

    async editNote(note) {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Edit Note',
                mode: 'textarea',
                dialogValue: note.notes,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (result.result && !!result.value) {
                note.notes = result.value;
                this.uow.commit([note]);
            }
        });
    }

    async deleteNote(note) {
        note.deleted = new Date();
        note.deletedBy = this.auth.currentUser.userName;
        this.notes = this.notes.filter((x) => x != note);
        this.uow.commit([note]);
    }
}
